// Analytic intersection/distance helpers used by hit-testing (05) and snapping (06).
import { EPS, dist, normalizeDeg, sub, type Bounds, type Vec } from "./geometry";

export function closestOnSeg(p: Vec, a: Vec, b: Vec): Vec {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  if (len2 < EPS * EPS) return a;
  let t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2;
  t = Math.max(0, Math.min(1, t));
  return { x: a.x + t * abx, y: a.y + t * aby };
}

export function distPointSeg(p: Vec, a: Vec, b: Vec): number {
  return dist(p, closestOnSeg(p, a, b));
}

/** Segment-segment intersection point (inclusive of endpoints), or null. */
export function segSegIntersect(p1: Vec, p2: Vec, p3: Vec, p4: Vec): Vec | null {
  const d1 = sub(p2, p1);
  const d2 = sub(p4, p3);
  const denom = d1.x * d2.y - d1.y * d2.x;
  if (Math.abs(denom) < EPS) return null; // parallel/collinear: no unique point
  const t = ((p3.x - p1.x) * d2.y - (p3.y - p1.y) * d2.x) / denom;
  const u = ((p3.x - p1.x) * d1.y - (p3.y - p1.y) * d1.x) / denom;
  const tol = 1e-9;
  if (t < -tol || t > 1 + tol || u < -tol || u > 1 + tol) return null;
  return { x: p1.x + t * d1.x, y: p1.y + t * d1.y };
}

/** Infinite line (point+dir) intersection, or null when parallel. */
export function lineLineIntersect(p1: Vec, d1: Vec, p2: Vec, d2: Vec): Vec | null {
  const denom = d1.x * d2.y - d1.y * d2.x;
  if (Math.abs(denom) < EPS) return null;
  const t = ((p2.x - p1.x) * d2.y - (p2.y - p1.y) * d2.x) / denom;
  return { x: p1.x + t * d1.x, y: p1.y + t * d1.y };
}

/** Segment-circle intersection points (t clipped to [0,1]). */
export function segCircleIntersect(a: Vec, b: Vec, center: Vec, r: number): Vec[] {
  const d = sub(b, a);
  const f = sub(a, center);
  const A = d.x * d.x + d.y * d.y;
  if (A < EPS * EPS) return [];
  const B = 2 * (f.x * d.x + f.y * d.y);
  const C = f.x * f.x + f.y * f.y - r * r;
  const disc = B * B - 4 * A * C;
  if (disc < 0) return [];
  const sq = Math.sqrt(disc);
  const out: Vec[] = [];
  for (const t of [(-B - sq) / (2 * A), (-B + sq) / (2 * A)]) {
    if (t >= -1e-9 && t <= 1 + 1e-9) out.push({ x: a.x + t * d.x, y: a.y + t * d.y });
  }
  if (out.length === 2 && dist(out[0]!, out[1]!) < EPS) out.pop(); // tangent
  return out;
}

export function circleCircleIntersect(c1: Vec, r1: number, c2: Vec, r2: number): Vec[] {
  const d = dist(c1, c2);
  if (d < EPS || d > r1 + r2 + EPS || d < Math.abs(r1 - r2) - EPS) return [];
  const a = (r1 * r1 - r2 * r2 + d * d) / (2 * d);
  const h2 = r1 * r1 - a * a;
  const h = h2 > 0 ? Math.sqrt(h2) : 0;
  const mx = c1.x + (a * (c2.x - c1.x)) / d;
  const my = c1.y + (a * (c2.y - c1.y)) / d;
  const ox = (h * (c2.y - c1.y)) / d;
  const oy = (h * (c2.x - c1.x)) / d;
  const p1 = { x: mx + ox, y: my - oy };
  const p2 = { x: mx - ox, y: my + oy };
  return h < EPS ? [p1] : [p1, p2];
}

export function pointInRect(p: Vec, r: Bounds): boolean {
  return p.x >= r.minX && p.x <= r.maxX && p.y >= r.minY && p.y <= r.maxY;
}

export function rectsOverlap(a: Bounds, b: Bounds): boolean {
  return a.minX <= b.maxX && a.maxX >= b.minX && a.minY <= b.maxY && a.maxY >= b.minY;
}

export function rectContainsRect(outer: Bounds, inner: Bounds): boolean {
  return (
    inner.minX >= outer.minX && inner.maxX <= outer.maxX && inner.minY >= outer.minY && inner.maxY <= outer.maxY
  );
}

/** Does segment a-b touch rect (for crossing marquee)? */
export function segRectIntersects(a: Vec, b: Vec, r: Bounds): boolean {
  if (pointInRect(a, r) || pointInRect(b, r)) return true;
  const c1 = { x: r.minX, y: r.minY };
  const c2 = { x: r.maxX, y: r.minY };
  const c3 = { x: r.maxX, y: r.maxY };
  const c4 = { x: r.minX, y: r.maxY };
  return (
    segSegIntersect(a, b, c1, c2) !== null ||
    segSegIntersect(a, b, c2, c3) !== null ||
    segSegIntersect(a, b, c3, c4) !== null ||
    segSegIntersect(a, b, c4, c1) !== null
  );
}

export function circleRectIntersects(center: Vec, r: number, rect: Bounds): boolean {
  const cx = Math.max(rect.minX, Math.min(center.x, rect.maxX));
  const cy = Math.max(rect.minY, Math.min(center.y, rect.maxY));
  const inside = pointInRect(center, rect);
  const d = dist(center, { x: cx, y: cy });
  if (!inside) return d <= r;
  // center inside: intersects unless the whole circle is inside AND we want boundary-touch only —
  // for crossing-marquee purposes an enclosed circle counts as touching.
  return true;
}

export function pointInPolygon(p: Vec, pts: Vec[]): boolean {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i]!;
    const b = pts[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) {
      inside = !inside;
    }
  }
  return inside;
}

/** Is angle `deg` on the arc from startDeg sweeping sweepDeg (ARC-4 convention)? */
export function arcContainsAngle(startDeg: number, sweepDeg: number, deg: number): boolean {
  const rel = normalizeDeg(deg - startDeg);
  if (sweepDeg >= 0) return rel <= sweepDeg + 1e-9;
  return normalizeDeg(-rel) <= -sweepDeg + 1e-9 || rel < 1e-9;
}

export function arcPoint(center: Vec, r: number, deg: number): Vec {
  const rad = (deg * Math.PI) / 180;
  return { x: center.x + r * Math.cos(rad), y: center.y + r * Math.sin(rad) };
}

/** Circumcenter of triangle abc, or null when (near-)collinear (EC-TL-7). */
export function circumcenter(a: Vec, b: Vec, c: Vec): Vec | null {
  const d = 2 * (a.x * (b.y - c.y) + b.x * (c.y - a.y) + c.x * (a.y - b.y));
  if (Math.abs(d) < 1e-6) return null;
  const a2 = a.x * a.x + a.y * a.y;
  const b2 = b.x * b.x + b.y * b.y;
  const c2 = c.x * c.x + c.y * c.y;
  return {
    x: (a2 * (b.y - c.y) + b2 * (c.y - a.y) + c2 * (a.y - b.y)) / d,
    y: (a2 * (c.x - b.x) + b2 * (a.x - c.x) + c2 * (b.x - a.x)) / d
  };
}
